import React, { useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import { useSecureKeyStore } from '../../stores/secureKeyStore';
import type { AuthCredentials } from '../../types/secureKey';
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ReAuthenticationModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    title?: string;
    description?: string;
}

export const ReAuthenticationModal: React.FC<ReAuthenticationModalProps> = ({
    open,
    onClose,
    onSuccess,
    title = 'Re-authenticate for Patient Data Access',
    description = 'To access patient data in reports, please re-enter your credentials for security verification.',
}) => {
    const [credentials, setCredentials] = useState<AuthCredentials>({
        emailOrUsername: '',
        password: '',
    });
    const [localError, setLocalError] = useState<string | null>(null);
    const { authenticate, isLoading, error, clearError } = useSecureKeyStore();
    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };
    const handleMouseUpPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        clearError();

        if (!credentials.emailOrUsername.trim()) {
            setLocalError('Epost eller användarnamn måste anges');
            return;
        }

        if (!credentials.password.trim()) {
            setLocalError('Lösenord måste anges');
            return;
        }

        try {
            await authenticate(credentials);

            // Clear form on success
            setCredentials({ emailOrUsername: '', password: '' });
            setLocalError(null);

            // Call success callback and close modal
            onSuccess?.();
            onClose();
        } catch (err) {
            // Error is handled by the store, but we can add local handling if needed
            console.error('Authentication failed:', err);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setCredentials({ emailOrUsername: '', password: '' });
            setLocalError(null);
            clearError();
            onClose();
        }
    };

    const displayError = localError || error;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={isLoading}
        >
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}
            >
                <SecurityIcon color="primary" />
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {title}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        {description}
                    </Typography>

                    {displayError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {displayError}
                        </Alert>
                    )}

                    <TextField
                        autoFocus
                        margin="dense"
                        label="Epost eller Användarnamn"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={credentials.emailOrUsername}
                        onChange={(e) =>
                            setCredentials((prev) => ({
                                ...prev,
                                emailOrUsername: e.target.value,
                            }))
                        }
                        disabled={isLoading}
                        required
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        margin="dense"
                        label="Lösenord"
                        type={showPassword ? 'text' : 'password'} // 👈 toggles password visibility
                        fullWidth
                        variant="outlined"
                        value={credentials.password}
                        onChange={(e) =>
                            setCredentials((prev) => ({
                                ...prev,
                                password: e.target.value,
                            }))
                        }
                        disabled={isLoading}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                showPassword
                                                    ? faEyeSlash
                                                    : faEye
                                            }
                                            className="text-gray-400"
                                        />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleClose}
                        disabled={isLoading}
                        color="inherit"
                    >
                        Avbryt
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            isLoading ||
                            !credentials.emailOrUsername.trim() ||
                            !credentials.password.trim()
                        }
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SecurityIcon />
                            )
                        }
                    >
                        {isLoading ? 'Autentiserar...' : 'Autentisera'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ReAuthenticationModal;
